import { useMaskableField } from "@/context/FormFieldVisibilityContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { SupportedLocale, supportedLocales } from "@/i18n";
import { cn } from "@/lib/utils";
import { SelectProps } from "@radix-ui/react-select";
import { HTMLProps, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export const MultilingualSelect = ({
  values,
  ...props
}: SelectProps & { values?: SupportedLocale[] }) => {
  const { t } = useTranslation();
  return (
    <Select {...props}>
      <SelectTrigger className="w-fit font-semibold">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(values ?? supportedLocales).map((locale) => (
          <SelectItem key={locale} value={locale}>
            {t(`i18n.languages.${locale}.minified`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export type MultilingualFieldProps = (
  | {
      fixed: SupportedLocale;
      defaultLang?: never;
      excludeLang?: never;
    }
  | {
      defaultLang?: SupportedLocale;
      excludeLang?: SupportedLocale;
      fixed?: never;
    }
) & {
  corner?: (selectedLocale: SupportedLocale) => ReactNode;
  render: (selectedLocale: SupportedLocale) => ReactNode;
  locales?: SupportedLocale[];
} & HTMLProps<HTMLDivElement>;

export const MultilingualField = ({
  render,
  fixed,
  defaultLang,
  excludeLang,
  locales,
  className,
  corner,
  name,
  ...props
}: MultilingualFieldProps) => {
  const { t } = useTranslation();
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();
  // When a field name is provided, mirror its masking state so the language
  // box is hidden entirely (not left as an empty framed block) once masked.
  const { minimized: fieldMinimized } = useMaskableField(name ?? "");

  const baseLocales = (locales ?? websiteLanguages) as SupportedLocale[];

  const availableLocales = useMemo(
    () =>
      (fixed
        ? [fixed]
        : excludeLang
          ? baseLocales.filter((l) => l !== excludeLang)
          : baseLocales) as SupportedLocale[],
    [baseLocales, fixed, excludeLang],
  );

  const preferredLocale =
    fixed ??
    defaultLang ??
    (defaultWebsiteLanguage && availableLocales.includes(defaultWebsiteLanguage)
      ? defaultWebsiteLanguage
      : availableLocales[0]);

  // Choix explicite de l'utilisateur (undefined = il n'a encore rien choisi).
  const [pickedLocale, setPickedLocale] = useState<SupportedLocale | undefined>(
    undefined,
  );

  // Langue effectivement éditée : état DÉRIVÉ, jamais synchronisé par un effet.
  // Un useEffect de synchro re-épinglait `defaultLang` / `defaultWebsiteLanguage`
  // dès que la config de langues changeait d'identité (chaque refetch de
  // general-settings en renvoie une nouvelle, ses URL S3 sont signées), ce qui
  // annulait le choix de l'utilisateur dès qu'il changeait de langue.
  // Ici : on garde son choix tant qu'il est valide, sinon on retombe sur la
  // langue préférée (donc aussi quand les general-settings finissent de charger,
  // ou quand une langue est retirée du site).
  const selectedLocale =
    pickedLocale && availableLocales.includes(pickedLocale)
      ? pickedLocale
      : preferredLocale;

  const languageConfigKey = `${defaultWebsiteLanguage}:${availableLocales.join(",")}`;

  if (name && fieldMinimized) {
    // Keep the field mounted (so it stays registered and re-showable from the
    // fields drawer) but drop its framed box so no empty block is left.
    return (
      <div className="hidden" aria-hidden>
        {render(selectedLocale)}
      </div>
    );
  }

  return (
    <div
      key={fixed ? `fixed-${fixed}-${languageConfigKey}` : languageConfigKey}
      className={cn(
        "flex flex-col rounded-2xl border border-gray-300",
        className,
      )}
      {...props}
    >
      <div className="rounded-t-2xl h-14 bg-gray-50 border-b border-gray-300 py-2 px-4 flex items-center justify-between">
        {fixed ? (
          <div className="flex items-center">
            <span className="text-sm font-semibold ml-2">
              {t(`i18n.languages.${fixed}.minified`)}
            </span>
          </div>
        ) : (
          <div className="flex items-center">
            <MultilingualSelect
              values={availableLocales}
              value={selectedLocale}
              onValueChange={(value) =>
                setPickedLocale(value as SupportedLocale)
              }
            />
          </div>
        )}
        {corner && <div key={selectedLocale}>{corner(selectedLocale)}</div>}
      </div>
      <div key={selectedLocale} className="p-4">
        {render(selectedLocale)}
      </div>
    </div>
  );
};

export const DoubleMultilingualField = ({
  className,
  ...props
}: Omit<
  MultilingualFieldProps,
  "fixed" | "defaultLang" | "excludeLang" | "locales"
>) => {
  const { defaultWebsiteLanguage, secondaryLanguages } =
    useCmsWebsiteLanguages();
  const fieldName = (props as { name?: string }).name;
  const { minimized: fieldMinimized } = useMaskableField(fieldName ?? "");

  const languageConfigKey = `${defaultWebsiteLanguage}:${secondaryLanguages.join(",")}`;

  return (
    <div
      key={languageConfigKey}
      className={cn(
        "flex w-full gap-4",
        // Masked: keep children mounted (registered/re-showable) but hidden.
        fieldName && fieldMinimized && "hidden",
        className,
      )}
    >
      <MultilingualField
        className="flex-1"
        {...props}
        fixed={defaultWebsiteLanguage}
      />
      {secondaryLanguages.length === 1 ? (
        <MultilingualField
          className="flex-1"
          {...props}
          fixed={secondaryLanguages[0]}
        />
      ) : secondaryLanguages.length > 1 ? (
        <MultilingualField
          className="flex-1"
          {...props}
          locales={secondaryLanguages}
          defaultLang={secondaryLanguages[0]}
        />
      ) : null}
    </div>
  );
};
